import type { DestinationContext, TrackingDestination } from '../types'

declare global {
  interface Window {
    dataLayer?: any[]
  }
}

/**
 * GA4 ecommerce events whose monetary/item fields must be nested under an
 * `ecommerce` object in the dataLayer (GA4 spec). For these, GTM's GA4 Event
 * tag should have "Send Ecommerce data" = "Data Layer". A flat push (items/value
 * at the top level) is NOT read as ecommerce and silently drops items/revenue.
 * @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
 */
const GA4_ECOMMERCE_EVENTS = new Set([
  'view_item',
  'view_item_list',
  'select_item',
  'add_to_cart',
  'remove_from_cart',
  'view_cart',
  'add_to_wishlist',
  'begin_checkout',
  'add_shipping_info',
  'add_payment_info',
  'purchase',
  'refund',
])

/** Fields that belong inside the GA4 `ecommerce` object rather than at event top level. */
const GA4_ECOMMERCE_FIELDS = new Set([
  'currency',
  'value',
  'transaction_id',
  'tax',
  'shipping',
  'coupon',
  'items',
  'item_list_id',
  'item_list_name',
])

export class Ga4Destination implements TrackingDestination {
  public readonly key = 'ga4' as const
  public readonly requiredConsent = 'statistics' as const

  init() {
    // No-op. GTM handles destination configuration.
  }

  identify(userId: string, traits?: Record<string, any>, ctx?: DestinationContext) {
    if (typeof window === 'undefined') return
    const preferredUserId = traits?.customerId ?? ctx?.identity?.customerId ?? userId
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'tracking_identify',
      user_id: preferredUserId,
    })
  }

  track(eventName: string, properties?: Record<string, any>) {
    if (typeof window === 'undefined') return
    window.dataLayer = window.dataLayer || []

    if (GA4_ECOMMERCE_EVENTS.has(eventName)) {
      const props = properties || {}
      const ecommerce: Record<string, any> = {}
      const rest: Record<string, any> = {}
      for (const [key, val] of Object.entries(props)) {
        if (GA4_ECOMMERCE_FIELDS.has(key)) {
          ecommerce[key] = val
        } else {
          rest[key] = val
        }
      }
      // GA4 expects currency as an uppercase ISO 4217 code (e.g. "USD", "GBP").
      // Callers may pass it lowercase, so normalise here for all ecommerce events,
      // including any per-item currency.
      if (typeof ecommerce.currency === 'string') {
        ecommerce.currency = ecommerce.currency.toUpperCase()
      }
      if (Array.isArray(ecommerce.items)) {
        ecommerce.items = ecommerce.items.map(item =>
          item && typeof item.currency === 'string'
            ? { ...item, currency: item.currency.toUpperCase() }
            : item
        )
      }
      // Clear any previous ecommerce object so item/value data can't leak
      // between consecutive ecommerce events (GTM merges dataLayer state).
      window.dataLayer.push({ ecommerce: null })
      window.dataLayer.push({
        event: eventName,
        ecommerce,
        ...rest,
      })
      return
    }

    window.dataLayer.push({
      event: eventName,
      ...(properties || {}),
    })
  }

  page(path: string, properties?: Record<string, any>) {
    if (typeof window === 'undefined') return
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'page_view',
      page_path: path,
      ...(properties || {}),
    })
  }
}


